// src/pages/ProductUpdatePage.tsx
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Card,
  Collapse,
  Form,
  Input,
  Select,
  Button,
  Typography,
  InputNumber,
  Switch,
  Row,
  Col,
  Spin,
  Tag,
  Table,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getProductApi, updateProductApi } from "src/api/product/productApi";
import { uploadImageApi, deleteFileApi } from "src/api/storage/storageApi";
import { getDelegateListApi } from "src/api/delegate/delegateApi";
import type { DelegateListItem } from "src/api/delegate/delegateApi";
import type {
  ProductUpsertBody,
  ProductType,
  Keyword,
  KeywordType,
  DeliveryMapping,
  AddonMapping,
  Variant,
  MainInfo,
  CollectionMapping,
  Discount,
  VolumePrice,
  OptionSelectionType,
  OptionGroupUpdate,
} from "src/types/product";
import { snackbarStore } from "src/store/snackbarStore";
import CustomButton from "src/components/common/CustomButton";
import ProductAddonSection from "src/components/product/ProductAddonSection";
import ProductVariantSection from "src/components/product/ProductVariantSection";
import HtmlEditor from "src/components/common/HtmlEditor";
import AppInput from "src/components/common/AppInput";
import AppInputNumber from "src/components/common/AppInputNumber";
import BrandSelectModal from "src/components/product/BrandSelectModal";
import CollectionSelectModal, {
  type SelectedCollectionItem,
} from "src/components/product/CollectionSelectModal";
import ReviewIntegrationProductSelectModal, {
  type SelectedProductItem,
} from "src/components/product/ReviewIntegrationProductSelectModal";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

type AdditionalImageItem = { fileId: number; fileUrl: string };

export default function ProductUpdatePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  const [productType, setProductType] = React.useState<ProductType>("SINGLE");
  const [addonMappings, setAddonMappings] = React.useState<AddonMapping[]>([]);
  const [initialVariants, setInitialVariants] = React.useState<Variant[]>([]);
  const variantsRef = React.useRef<Variant[]>([]);
  const [keywords, setKeywords] = React.useState<Keyword[]>([]);

  // 옵션 관련
  const [optionSelectionType, setOptionSelectionType] =
    React.useState<OptionSelectionType>("INDEPENDENT");
  const [optionGroups, setOptionGroups] = React.useState<OptionGroupUpdate[]>([]);
  const [keywordData, setKeywordData] = React.useState<{
    keyword: string;
    keywordType: KeywordType;
    weight: number;
    isActive: boolean;
  }>({
    keyword: "",
    keywordType: "GENERAL",
    weight: 1,
    isActive: true,
  });
  const [deliveryMappings, setDeliveryMappings] = React.useState<DeliveryMapping[]>([]);
  const [selectedCollections, setSelectedCollections] = React.useState<SelectedCollectionItem[]>(
    []
  );

  // 리뷰 합침 상품
  const [selectedReviewProducts, setSelectedReviewProducts] = React.useState<SelectedProductItem[]>(
    []
  );
  const [reviewProductModalOpen, setReviewProductModalOpen] = React.useState(false);

  // 관련상품
  const [selectedRelatedProducts, setSelectedRelatedProducts] = React.useState<SelectedProductItem[]>(
    []
  );
  const [relatedProductModalOpen, setRelatedProductModalOpen] = React.useState(false);

  // 추천상품
  const [selectedRecommendedProducts, setSelectedRecommendedProducts] = React.useState<SelectedProductItem[]>(
    []
  );
  const [recommendedProductModalOpen, setRecommendedProductModalOpen] = React.useState(false);

  // 재고 정보
  const [stockInfo, setStockInfo] = React.useState<
    Array<{ variantId?: number; variantName?: string; sellableQuantity: number }>
  >([]);

  // 즉시할인
  const [instantDiscountEnabled, setInstantDiscountEnabled] = React.useState(false);
  const [discounts, setDiscounts] = React.useState<Discount[]>([]);

  // 수량별 할인
  const [volumePrices, setVolumePrices] = React.useState<VolumePrice[]>([]);

  // 대표 이미지
  const [mainImageFileGrpId, setMainImageFileGrpId] = React.useState<number | null>(null);
  const [mainImageFileId, setMainImageFileId] = React.useState<number | null>(null);
  const [mainImagePreview, setMainImagePreview] = React.useState<string>("");
  const [mainImageUploading, setMainImageUploading] = React.useState(false);

  // 추가 이미지
  const [additionalImageFileGrpId, setAdditionalImageFileGrpId] = React.useState<number | null>(
    null
  );
  const [additionalImageItems, setAdditionalImageItems] = React.useState<AdditionalImageItem[]>([]);
  const [additionalImageUploading, setAdditionalImageUploading] = React.useState(false);

  const mainImageInputRef = React.useRef<HTMLInputElement | null>(null);
  const additionalInputRef = React.useRef<HTMLInputElement | null>(null);
  const [additionalReplaceIndex, setAdditionalReplaceIndex] = React.useState<number | null>(null);

  // 브랜드 / 위탁사 / 콜렉션
  const [selectedBrandId, setSelectedBrandId] = React.useState<number | null>(null);
  const [selectedBrandName, setSelectedBrandName] = React.useState<string | null>(null);
  const [brandModalOpen, setBrandModalOpen] = React.useState(false);
  const [collectionModalOpen, setCollectionModalOpen] = React.useState(false);
  const [delegateList, setDelegateList] = React.useState<DelegateListItem[]>([]);

  React.useEffect(() => {
    getDelegateListApi({ page: 0, size: 1000, order: "delegateName,asc" })
      .then((res) => {
        if (res.statusCode === 1 && res.data) setDelegateList(res.data.content);
      })
      .catch(() => {});
  }, []);

  // 상품 정보 불러오기
  React.useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        toast("상품 ID가 없습니다.", "error");
        navigate("/product");
        return;
      }

      const numericId = Number(id);
      if (isNaN(numericId)) {
        toast(`유효하지 않은 상품 ID입니다. (${id})`, "error");
        navigate("/product");
        return;
      }

      try {
        setLoading(true);
        const res = await getProductApi(numericId);

        if (res.statusCode !== 1 || !res.data) {
          toast(res.message || "상품 정보를 불러올 수 없습니다.", "error");
          navigate("/product");
          return;
        }

        const product = res.data;

        // Form 값 설정
        form.setFieldsValue({
          productCode: product.productCode,
          productName: product.productName,
          productDescription: product.productDescription,
          productType: product.productType,
          saleStatus: product.saleStatus,
          delegateId: product.delegateId,
          brandId: product.brandId,
          petType: product.petType,
          targetAge: product.targetAge,
          targetSize: product.targetSize,
          taxType: product.taxType || "TAXABLE",
          baseSalePrice: product.baseSalePrice,
          ingredients: product.productDetail?.ingredients,
          precautions: product.productDetail?.precautions,
          careGuide: product.productDetail?.careGuide,
          detailType: product.productDetail?.detailType || "HTML",
          detailContent: product.productDetail?.detailContent,
          mobileDetailContent: product.productDetail?.mobileDetailContent,
          kcCertified: product.mainInfo?.kcCertified || false,
          kcCertificationNumber: product.mainInfo?.kcCertificationNumber,
          originCountry: product.mainInfo?.originCountry,
          productStatus: product.mainInfo?.productStatus || "NORMAL",
          manufactureDate: product.mainInfo?.manufactureDate,
          releaseDate: product.mainInfo?.releaseDate,
          isDisplay: product.isDisplay,
          displayOrder: product.displayOrder,
          displayStartDate: product.displayStartDate,
          displayEndDate: product.displayEndDate,
          minQuantityPerOrder: product.minQuantityPerOrder ?? undefined,
          maxQuantityPerOrder: product.maxQuantityPerOrder ?? undefined,
        });

        console.log("Form values after setFieldsValue:", form.getFieldsValue());
        console.log("baseSalePrice from form:", form.getFieldValue("baseSalePrice"));

        setProductType(product.productType);
        setKeywords(product.keywords || []);
        setAddonMappings(product.addonMappings || []);
        setInitialVariants(product.variants || []);
        variantsRef.current = product.variants || [];
        setDeliveryMappings(product.deliveryMappings || []);

        // 옵션 관련 정보 설정
        if (product.productType === "OPTION" && product.optionSelectionType) {
          setOptionSelectionType(product.optionSelectionType);
        }
        if (product.optionGroups) {
          setOptionGroups(product.optionGroups);
        }
        setSelectedBrandId(product.brandId ?? null);
        setSelectedBrandName(product.brandName ?? null);

        // collectionMappings 처리 (collectionName이 포함되어 있다고 가정)
        const collectionMappings = (product as any).collectionMappings || [];
        setSelectedCollections(
          collectionMappings.map((c: any) => ({
            collectionId: c.collectionId,
            collectionName: c.collectionName || `Collection ${c.collectionId}`,
          }))
        );

        // reviewIntegrations 처리
        const reviewIntegrations = (product as any).reviewIntegrations || [];
        if (reviewIntegrations.length > 0) {
          setSelectedReviewProducts(
            reviewIntegrations.map((r: any) => ({
              productId: r.sourceProductId,
              productName: r.sourceProductName,
              productCode: r.sourceProductCode,
            }))
          );
        }

        // relatedProducts 처리
        const relatedProducts = (product as any).relatedProducts || [];
        if (relatedProducts.length > 0) {
          setSelectedRelatedProducts(
            relatedProducts.map((r: any) => ({
              productId: r.relatedProductId,
              productName: r.relatedProductName,
              productCode: r.relatedProductCode,
            }))
          );
        }

        // recommendedProducts 처리
        const recommendedProducts = (product as any).recommendedProducts || [];
        if (recommendedProducts.length > 0) {
          setSelectedRecommendedProducts(
            recommendedProducts.map((r: any) => ({
              productId: r.relatedProductId,
              productName: r.relatedProductName,
              productCode: r.relatedProductCode,
            }))
          );
        }

        // stockInfo 처리
        const stockInfoData = (product as any).stockInfo || [];
        setStockInfo(stockInfoData);

        // 즉시할인 처리
        const productDiscounts = (product as any).discounts || [];
        setDiscounts(productDiscounts);
        setInstantDiscountEnabled((product as any).instantDiscountEnabled || false);

        // 수량별 할인 처리
        const productVolumePrices = (product as any).volumePrices || [];
        setVolumePrices(productVolumePrices);

        // 기존 대표 이미지
        if (product.mainImage) {
          setMainImageFileGrpId(product.mainImage.fileGrpId ?? null);
          const firstFile = product.mainImage.files?.[0];
          if (firstFile) {
            setMainImageFileId(firstFile.fileId);
            setMainImagePreview(firstFile.fileUrl);
          }
        }

        // 기존 추가 이미지 (첫 번째 그룹의 파일들을 사용)
        if (product.additionalImages && product.additionalImages.length > 0) {
          const group = product.additionalImages[0];
          setAdditionalImageFileGrpId(group.fileGrpId ?? null);
          setAdditionalImageItems(
            (group.files || []).map((f) => ({ fileId: f.fileId, fileUrl: f.fileUrl }))
          );
        }
      } catch (e: any) {
        toast(e?.message || "상품 정보를 불러오는 중 오류가 발생했습니다.", "error");
        navigate("/product");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, navigate, form]);

  // 대표 이미지 선택/교체
  const handleMainImageSelect = async (file: File) => {
    setMainImageUploading(true);
    try {
      if (mainImageFileId) {
        await deleteFileApi(mainImageFileId);
      }
      const res = await uploadImageApi("products/images/main", [file], undefined, true);
      if (res.statusCode === 1 && res.data) {
        const uploaded = res.data.files[0];
        setMainImageFileGrpId(res.data.fileGrpId);
        setMainImageFileId(uploaded.fileId);
        setMainImagePreview(uploaded.fileUrl);
      } else {
        toast(res.message || "이미지 업로드 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "이미지 업로드 중 오류 발생", "error");
    } finally {
      setMainImageUploading(false);
    }
  };

  // 대표 이미지 삭제
  const handleMainImageRemove = async () => {
    if (mainImageFileId) {
      try {
        await deleteFileApi(mainImageFileId);
      } catch {
        // silent
      }
    }
    setMainImageFileGrpId(null);
    setMainImageFileId(null);
    setMainImagePreview("");
  };

  // 추가 이미지 추가
  const handleAdditionalImageSelect = async (file: File) => {
    setAdditionalImageUploading(true);
    try {
      const res = await uploadImageApi("products/images/additional", [file], additionalImageFileGrpId ?? undefined, false);
      if (res.statusCode === 1 && res.data) {
        setAdditionalImageFileGrpId(res.data.fileGrpId);
        const existingIds = new Set(additionalImageItems.map((item) => item.fileId));
        const newFile = res.data.files.find((f) => !existingIds.has(f.fileId));
        if (newFile) {
          setAdditionalImageItems((prev) => [
            ...prev,
            { fileId: newFile.fileId, fileUrl: newFile.fileUrl },
          ]);
        }
      } else {
        toast(res.message || "이미지 업로드 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "이미지 업로드 중 오류 발생", "error");
    } finally {
      setAdditionalImageUploading(false);
    }
  };

  // 추가 이미지 교체
  const handleAdditionalImageReplace = async (index: number, file: File) => {
    const oldItem = additionalImageItems[index];
    setAdditionalImageUploading(true);
    try {
      if (oldItem) {
        await deleteFileApi(oldItem.fileId);
      }
      const remainingIds = new Set(
        additionalImageItems.filter((_, i) => i !== index).map((item) => item.fileId)
      );
      const res = await uploadImageApi("products/images/additional", [file], additionalImageFileGrpId ?? undefined, false);
      if (res.statusCode === 1 && res.data) {
        setAdditionalImageFileGrpId(res.data.fileGrpId);
        const newFile = res.data.files.find((f) => !remainingIds.has(f.fileId));
        if (newFile) {
          setAdditionalImageItems((prev) => {
            const next = [...prev];
            next[index] = { fileId: newFile.fileId, fileUrl: newFile.fileUrl };
            return next;
          });
        }
      } else {
        toast(res.message || "이미지 업로드 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "이미지 업로드 중 오류 발생", "error");
    } finally {
      setAdditionalImageUploading(false);
    }
  };

  const handleAdditionalInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (additionalReplaceIndex == null) {
      await handleAdditionalImageSelect(file);
    } else {
      await handleAdditionalImageReplace(additionalReplaceIndex, file);
    }
    e.target.value = "";
    setAdditionalReplaceIndex(null);
  };

  const openAdditionalPicker = (index: number | null) => {
    setAdditionalReplaceIndex(index);
    additionalInputRef.current?.click();
  };

  // 추가 이미지 삭제
  const handleAdditionalImageRemove = async (index: number) => {
    const item = additionalImageItems[index];
    if (item) {
      try {
        await deleteFileApi(item.fileId);
      } catch {
        // silent
      }
    }
    const newItems = additionalImageItems.filter((_, i) => i !== index);
    setAdditionalImageItems(newItems);
    if (newItems.length === 0) {
      setAdditionalImageFileGrpId(null);
    }
  };

  const handleFormValuesChange = () => {
    // Form values change handler (no automatic calculations)
  };

  const handleSubmit = async (values: any) => {
    if (!id) return;

    if (!mainImageFileGrpId) {
      toast("대표 이미지를 선택해주세요.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const mainInfo: MainInfo = {
        kcCertified: values.kcCertified ?? false,
        kcCertificationNumber: values.kcCertificationNumber,
        originCountry: values.originCountry || "",
        productStatus: values.productStatus || "NORMAL",
        manufactureDate: values.manufactureDate,
        releaseDate: values.releaseDate,
      };

      const collectionMappings: CollectionMapping[] = selectedCollections.map((c) => ({
        collectionId: c.collectionId,
        displayOrder: 0,
        isActive: true,
      }));

      const payload: ProductUpsertBody = {
        productCode: values.productCode,
        productName: values.productName,
        productDescription: values.productDescription,
        productType,
        saleStatus: values.saleStatus || "PREPARING",

        delegateId: values.delegateId,
        brandId: selectedBrandId ?? undefined,

        petType: values.petType || "DOG",
        targetAge: values.targetAge || "ALL",
        targetSize: values.targetSize || "ALL",

        taxType: values.taxType || "TAXABLE",
        baseSalePrice: values.baseSalePrice ?? 0,

        ingredients: values.ingredients,
        precautions: values.precautions,
        careGuide: values.careGuide,

        detailType: values.detailType || "HTML",
        detailContent: values.detailContent,
        mobileDetailContent: values.mobileDetailContent,

        mainInfo,
        keywords,
        addonMappings,

        // 옵션 관련
        optionSelectionType: productType === "OPTION" ? optionSelectionType : undefined,
        optionGroups: productType === "OPTION" ? optionGroups : undefined,
        variants: productType === "SINGLE" ? [] : variantsRef.current,

        deliveryMappings,
        collectionMappings,
        discounts: instantDiscountEnabled ? discounts : [],
        volumePrices,
        instantDiscountEnabled,

        isDisplay: values.isDisplay ?? true,
        displayOrder: values.displayOrder || 0,
        displayStartDate: values.displayStartDate,
        displayEndDate: values.displayEndDate,

        mainImageFileGrpId,
        additionalImageFileGrpId: additionalImageFileGrpId ?? undefined,

        reviewIntegrationProductIds: selectedReviewProducts.map((p) => p.productId),
        relatedProductIds: selectedRelatedProducts.map((p) => p.productId),
        recommendedProductIds: selectedRecommendedProducts.map((p) => p.productId),

        minQuantityPerOrder: values.minQuantityPerOrder ?? null,
        maxQuantityPerOrder: values.maxQuantityPerOrder ?? null,
      };

      const res = await updateProductApi(Number(id), payload);

      if (res.statusCode === 1) {
        toast("상품이 수정되었습니다.", "success");
        navigate("/product");
      } else {
        toast(res?.message || "상품 수정 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const optionPanel =
    productType === "OPTION"
      ? {
          key: "options",
          label: "옵션 정보",
          children: (
            <ProductVariantSection
              initialValue={initialVariants}
              valueRef={variantsRef}
              optionSelectionType={optionSelectionType}
              onOptionSelectionTypeChange={setOptionSelectionType}
              optionGroups={optionGroups}
              onOptionGroupsChange={setOptionGroups as any}
              isUpdate={true}
            />
          ),
        }
      : null;

  const collapseItems = [
    {
      key: "basic",
      label: "기본 정보",
      children: (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="상품코드" name="productCode">
                <AppInput placeholder="자동 생성 또는 입력" disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="상품명"
                name="productName"
                rules={[{ required: true, message: "상품명을 입력해주세요" }]}
              >
                <AppInput placeholder="상품명 입력" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="상품 타입" name="productType">
                <Select onChange={(value) => setProductType(value)}>
                  <Select.Option value="SINGLE">단일</Select.Option>
                  <Select.Option value="OPTION">옵션</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="판매 상태" name="saleStatus">
                <Select>
                  <Select.Option value="PREPARING">준비중</Select.Option>
                  <Select.Option value="ON_SALE">판매중</Select.Option>
                  <Select.Option value="SOLDOUT">품절</Select.Option>
                  <Select.Option value="SUSPENDED">판매중지</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="위탁사" name="delegateId">
                <Select placeholder="위탁사 선택" allowClear showSearch optionFilterProp="label">
                  <Select.Option value={null} label="없음">
                    없음
                  </Select.Option>
                  {delegateList.map((d) => (
                    <Select.Option key={d.id} value={d.id} label={d.delegateName}>
                      {d.delegateName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
                브랜드
              </Typography.Text>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                <CustomButton onClick={() => setBrandModalOpen(true)} style={{ padding: "0 30px" }}>
                  선택
                </CustomButton>
                {selectedBrandName && (
                  <Tag
                    closable
                    onClose={() => {
                      setSelectedBrandId(null);
                      setSelectedBrandName(null);
                    }}
                    style={{ marginTop: 4 }}
                  >
                    {selectedBrandName}
                  </Tag>
                )}
              </div>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
                콜렉션
              </Typography.Text>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                <CustomButton
                  onClick={() => setCollectionModalOpen(true)}
                  style={{ padding: "0 30px" }}
                >
                  선택
                </CustomButton>
                {selectedCollections.map((collection) => (
                  <Tag
                    key={collection.collectionId}
                    closable
                    onClose={() => {
                      setSelectedCollections((prev) =>
                        prev.filter((c) => c.collectionId !== collection.collectionId)
                      );
                    }}
                    style={{ marginTop: 4 }}
                  >
                    {collection.collectionName}
                  </Tag>
                ))}
              </div>
            </Col>
          </Row>

          <Form.Item label="상품 설명" name="productDescription">
            <Input.TextArea rows={3} placeholder="상품 설명 입력" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="최소 구매 수량"
                name="minQuantityPerOrder"
                extra="미입력 시 제한 없음"
              >
                <AppInputNumber
                  style={{ width: "100%" }}
                  min={1}
                  precision={0}
                  placeholder="최소 구매 수량"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="최대 구매 수량"
                name="maxQuantityPerOrder"
                extra="미입력 시 제한 없음"
              >
                <AppInputNumber
                  style={{ width: "100%" }}
                  min={1}
                  precision={0}
                  placeholder="최대 구매 수량"
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: "stock",
      label: "재고정보",
      children: (
        <>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="재고수량">
                <AppInputNumber
                  value={stockInfo.reduce(
                    (sum: number, item) => sum + (item.sellableQuantity || 0),
                    0
                  )}
                  disabled
                  suffix="개"
                />
              </Form.Item>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                연결된 SKU의 가용재고 수량만 자동됩니다.
              </Typography.Text>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: "photos",
      label: "사진 정보",
      children: (
        <>
          <div style={{ marginBottom: 24 }}>
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
              대표 이미지
            </Typography.Text>
            <div style={{ display: "flex", gap: 8 }}>
              {mainImagePreview ? (
                <div
                  style={{
                    position: "relative",
                    width: 120,
                    height: 120,
                    border: "1px solid #d9d9d9",
                    borderRadius: 8,
                    overflow: "hidden",
                    cursor: "pointer",
                  }}
                  onClick={() => !mainImageUploading && mainImageInputRef.current?.click()}
                >
                  <img
                    src={mainImagePreview}
                    alt="대표 이미지"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {mainImageUploading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255,255,255,0.7)",
                      }}
                    >
                      <Spin />
                    </div>
                  )}
                  <input
                    ref={mainImageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleMainImageSelect(file);
                      e.currentTarget.value = "";
                    }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMainImageRemove();
                    }}
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    }}
                  />
                </div>
              ) : (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 120,
                    height: 120,
                    border: "2px dashed #d9d9d9",
                    borderRadius: 8,
                    cursor: mainImageUploading ? "not-allowed" : "pointer",
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    if (!mainImageUploading) e.currentTarget.style.borderColor = "#1890ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#d9d9d9";
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    disabled={mainImageUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleMainImageSelect(file);
                    }}
                  />
                  {mainImageUploading ? (
                    <Spin />
                  ) : (
                    <PlusOutlined style={{ fontSize: 24, color: "#999" }} />
                  )}
                </label>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
              추가 이미지 ({additionalImageItems.length}/10)
            </Typography.Text>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {additionalImageItems.map((item, index) => (
                <div
                  key={item.fileId}
                  style={{
                    position: "relative",
                    width: 120,
                    height: 120,
                    border: "1px solid #d9d9d9",
                    borderRadius: 8,
                    overflow: "hidden",
                    cursor: "pointer",
                  }}
                  onClick={() => !additionalImageUploading && openAdditionalPicker(index)}
                >
                  <img
                    src={item.fileUrl}
                    alt={`추가 이미지 ${index + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAdditionalImageRemove(index);
                    }}
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    }}
                  />
                </div>
              ))}

              {additionalImageItems.length < 10 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 120,
                    height: 120,
                    border: "2px dashed #d9d9d9",
                    borderRadius: 8,
                    cursor: additionalImageUploading ? "not-allowed" : "pointer",
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    if (!additionalImageUploading) e.currentTarget.style.borderColor = "#1890ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#d9d9d9";
                  }}
                  onClick={() => !additionalImageUploading && openAdditionalPicker(null)}
                >
                  {additionalImageUploading ? (
                    <Spin />
                  ) : (
                    <PlusOutlined style={{ fontSize: 24, color: "#999" }} />
                  )}
                </div>
              )}
            </div>
            <input
              ref={additionalInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleAdditionalInputChange}
            />
          </div>
        </>
      ),
    },
    {
      key: "pet",
      label: "반려동물 정보",
      children: (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="펫 타입" name="petType">
              <Select>
                <Select.Option value="DOG">강아지</Select.Option>
                <Select.Option value="CAT">고양이</Select.Option>
                <Select.Option value="BOTH">모두</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="대상 연령" name="targetAge">
              <Select>
                <Select.Option value="PUPPY">퍼피</Select.Option>
                <Select.Option value="ADULT">어덜트</Select.Option>
                <Select.Option value="SENIOR">시니어</Select.Option>
                <Select.Option value="ALL">전체</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="대상 크기" name="targetSize">
              <Select>
                <Select.Option value="SMALL">소형</Select.Option>
                <Select.Option value="MEDIUM">중형</Select.Option>
                <Select.Option value="LARGE">대형</Select.Option>
                <Select.Option value="ALL">전체</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      ),
    },
    {
      key: "keyword",
      label: "검색 정보(키워드)",
      children: (
        <>
          <Typography.Text strong style={{ display: "block", marginBottom: 12 }}>
            키워드 추가
          </Typography.Text>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                키워드 타입
              </Typography.Text>
              <Select
                value={keywordData.keywordType}
                onChange={(value) => setKeywordData({ ...keywordData, keywordType: value })}
                style={{ width: "100%" }}
              >
                <Select.Option value="GENERAL">GENERAL</Select.Option>
                <Select.Option value="BRAND">BRAND</Select.Option>
                <Select.Option value="CATEGORY">CATEGORY</Select.Option>
                <Select.Option value="TAG">TAG</Select.Option>
              </Select>
            </div>
            <div style={{ flex: 2 }}>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                키워드
              </Typography.Text>
              <AppInput
                value={keywordData.keyword}
                onChange={(e) => setKeywordData({ ...keywordData, keyword: e.target.value })}
                placeholder="키워드 입력"
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                가중치
              </Typography.Text>
              <AppInputNumber
                value={keywordData.weight}
                onChange={(val) => setKeywordData({ ...keywordData, weight: val || 1 })}
                min={1}
                max={100}
                style={{ width: "100%" }}
              />
            </div>
            <CustomButton
              variant="contained"
              onClick={() => {
                if (!keywordData.keyword.trim()) {
                  toast("키워드를 입력해주세요.", "warning");
                  return;
                }
                setKeywords([...keywords, { ...keywordData }]);
                setKeywordData({
                  keyword: "",
                  keywordType: "GENERAL",
                  weight: 1,
                  isActive: true,
                });
                toast("키워드가 추가되었습니다.", "success");
              }}
            >
              추가하기
            </CustomButton>
          </div>

          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            키워드 목록 ({keywords.length}개)
          </Typography.Text>
          <Table
            columns={[
              {
                title: "키워드 타입",
                dataIndex: "keywordType",
                width: 150,
                align: "center",
              },
              {
                title: "키워드",
                dataIndex: "keyword",
                width: 250,
                align: "left",
              },
              {
                title: "가중치",
                dataIndex: "weight",
                width: 100,
                align: "center",
              },
              {
                title: "활성",
                dataIndex: "isActive",
                width: 100,
                align: "center",
                render: (value: boolean) => (
                  <Typography.Text>{value ? "활성" : "비활성"}</Typography.Text>
                ),
              },
              {
                title: "삭제",
                width: 80,
                align: "center",
                fixed: "right",
                render: (_: any, __: any, index: number) => (
                  <CustomButton
                    size="small"
                    customVariant="danger"
                    onClick={() => setKeywords(keywords.filter((_, i) => i !== index))}
                  >
                    삭제
                  </CustomButton>
                ),
              },
            ]}
            dataSource={keywords}
            rowKey={(_, index) => String(index)}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: 1000 }}
            locale={{ emptyText: "키워드를 추가해주세요." }}
          />
        </>
      ),
    },
    {
      key: "price",
      label: "가격 정보",
      children: (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="과세 유형" name="taxType">
                <Select>
                  <Select.Option value="TAXABLE">과세</Select.Option>
                  <Select.Option value="TAX_FREE">면세</Select.Option>
                  <Select.Option value="NON_TAXABLE">비과세</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="판매가" name="baseSalePrice">
                <AppInputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="판매가"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => Number(value?.replace(/,/g, "") || 0) as any}
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <Typography.Text strong>즉시할인</Typography.Text>
              <div style={{ display: "flex", gap: 4 }}>
                <Button
                  type={instantDiscountEnabled ? "primary" : "default"}
                  size="small"
                  onClick={() => setInstantDiscountEnabled(true)}
                  style={{
                    backgroundColor: instantDiscountEnabled ? "#52c41a" : undefined,
                    borderColor: instantDiscountEnabled ? "#52c41a" : undefined,
                  }}
                >
                  설정함
                </Button>
                <Button
                  type={!instantDiscountEnabled ? "primary" : "default"}
                  size="small"
                  onClick={() => {
                    setInstantDiscountEnabled(false);
                    setDiscounts([]);
                  }}
                  style={{
                    backgroundColor: !instantDiscountEnabled ? "#52c41a" : undefined,
                    borderColor: !instantDiscountEnabled ? "#52c41a" : undefined,
                  }}
                >
                  설정안함
                </Button>
              </div>
            </div>

            {instantDiscountEnabled && (
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                <Typography.Text strong style={{ display: "block", marginBottom: 12 }}>
                  기본할인
                </Typography.Text>
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                  판매가에서 즉시 할인이 가능한 할인 유형으로 할인된 가격으로 판매 됩니다.
                </Typography.Text>

                <div
                  style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "flex-start" }}
                >
                  <AppInputNumber
                    value={
                      discounts.find((d) => d.discountCategory === "BASIC")?.discountValue || 0
                    }
                    onChange={(val) => {
                      const basicDiscount = discounts.find((d) => d.discountCategory === "BASIC");
                      if (basicDiscount) {
                        setDiscounts(
                          discounts.map((d) =>
                            d.discountCategory === "BASIC" ? { ...d, discountValue: val || 0 } : d
                          )
                        );
                      } else {
                        setDiscounts([
                          ...discounts,
                          {
                            discountCategory: "BASIC",
                            discountType: "FIXED",
                            discountValue: val || 0,
                            isTimeLimited: false,
                            startDate: "",
                            endDate: "",
                          },
                        ]);
                      }
                    }}
                    min={0}
                    style={{ width: 200 }}
                  />
                  <Select
                    value={
                      discounts.find((d) => d.discountCategory === "BASIC")?.discountType || "FIXED"
                    }
                    onChange={(value) => {
                      const basicDiscount = discounts.find((d) => d.discountCategory === "BASIC");
                      if (basicDiscount) {
                        setDiscounts(
                          discounts.map((d) =>
                            d.discountCategory === "BASIC" ? { ...d, discountType: value } : d
                          )
                        );
                      }
                    }}
                    style={{ width: 80 }}
                  >
                    <Select.Option value="FIXED">원</Select.Option>
                    <Select.Option value="RATE">%</Select.Option>
                  </Select>
                  <Typography.Text>할인</Typography.Text>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        discounts.find((d) => d.discountCategory === "BASIC")?.isTimeLimited ||
                        false
                      }
                      onChange={(e) => {
                        const basicDiscount = discounts.find((d) => d.discountCategory === "BASIC");
                        if (basicDiscount) {
                          setDiscounts(
                            discounts.map((d) =>
                              d.discountCategory === "BASIC"
                                ? { ...d, isTimeLimited: e.target.checked }
                                : d
                            )
                          );
                        }
                      }}
                    />
                    <Typography.Text>특정기간만 할인</Typography.Text>
                  </label>
                </div>

                {discounts.find((d) => d.discountCategory === "BASIC")?.isTimeLimited && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <Input
                      type="datetime-local"
                      value={
                        discounts
                          .find((d) => d.discountCategory === "BASIC")
                          ?.startDate?.slice(0, 16) || ""
                      }
                      onChange={(e) => {
                        setDiscounts(
                          discounts.map((d) =>
                            d.discountCategory === "BASIC" ? { ...d, startDate: e.target.value } : d
                          )
                        );
                      }}
                    />
                    <Typography.Text>~</Typography.Text>
                    <Input
                      type="datetime-local"
                      value={
                        discounts
                          .find((d) => d.discountCategory === "BASIC")
                          ?.endDate?.slice(0, 16) || ""
                      }
                      onChange={(e) => {
                        setDiscounts(
                          discounts.map((d) =>
                            d.discountCategory === "BASIC" ? { ...d, endDate: e.target.value } : d
                          )
                        );
                      }}
                    />
                  </div>
                )}

                <div>
                  <Typography.Text type="secondary">할인가</Typography.Text>
                  <Typography.Text strong style={{ fontSize: 16, marginLeft: 8 }}>
                    {(() => {
                      const basicDiscount = discounts.find((d) => d.discountCategory === "BASIC");
                      if (!basicDiscount || !basicDiscount.discountValue) return "0원";
                      const salePrice = form.getFieldValue("baseSalePrice") || 0;
                      const discountedPrice =
                        basicDiscount.discountType === "FIXED"
                          ? salePrice - basicDiscount.discountValue
                          : salePrice * (1 - basicDiscount.discountValue / 100);
                      return `${Math.max(0, discountedPrice).toLocaleString()}원 (${basicDiscount.discountValue.toLocaleString()}${basicDiscount.discountType === "FIXED" ? "원" : "%"} 할인)`;
                    })()}
                  </Typography.Text>
                </div>
              </div>
            )}

            {instantDiscountEnabled && (
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: 16,
                  borderRadius: 8,
                }}
              >
                <Typography.Text strong style={{ display: "block", marginBottom: 12 }}>
                  예약할인
                </Typography.Text>
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                  예약할인은 기본할인보다 우선 적용되며, 할인 기간 종료 후에는 기본할인 또는 판매가
                  가격으로 적용됩니다.
                </Typography.Text>

                <div
                  style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "flex-start" }}
                >
                  <Typography.Text style={{ minWidth: 80 }}>판매가에서</Typography.Text>
                  <AppInputNumber
                    value={
                      discounts.find((d) => d.discountCategory === "RESERVATION")?.discountValue ||
                      0
                    }
                    onChange={(val) => {
                      const reserveDiscount = discounts.find(
                        (d) => d.discountCategory === "RESERVATION"
                      );
                      if (reserveDiscount) {
                        setDiscounts(
                          discounts.map((d) =>
                            d.discountCategory === "RESERVATION"
                              ? { ...d, discountValue: val || 0 }
                              : d
                          )
                        );
                      } else {
                        setDiscounts([
                          ...discounts,
                          {
                            discountCategory: "RESERVATION",
                            discountType: "FIXED",
                            discountValue: val || 0,
                            isTimeLimited: true,
                            startDate: "",
                            endDate: "",
                          },
                        ]);
                      }
                    }}
                    min={0}
                    style={{ width: 200 }}
                  />
                  <Select
                    value={
                      discounts.find((d) => d.discountCategory === "RESERVATION")?.discountType ||
                      "FIXED"
                    }
                    onChange={(value) => {
                      const reserveDiscount = discounts.find(
                        (d) => d.discountCategory === "RESERVATION"
                      );
                      if (reserveDiscount) {
                        setDiscounts(
                          discounts.map((d) =>
                            d.discountCategory === "RESERVATION" ? { ...d, discountType: value } : d
                          )
                        );
                      }
                    }}
                    style={{ width: 80 }}
                  >
                    <Select.Option value="FIXED">원</Select.Option>
                    <Select.Option value="RATE">%</Select.Option>
                  </Select>
                  <Typography.Text>할인</Typography.Text>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                    할인 기간
                  </Typography.Text>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Input
                      type="datetime-local"
                      value={
                        discounts
                          .find((d) => d.discountCategory === "RESERVATION")
                          ?.startDate?.slice(0, 16) || ""
                      }
                      onChange={(e) => {
                        const reserveDiscount = discounts.find(
                          (d) => d.discountCategory === "RESERVATION"
                        );
                        if (reserveDiscount) {
                          setDiscounts(
                            discounts.map((d) =>
                              d.discountCategory === "RESERVATION"
                                ? { ...d, startDate: e.target.value }
                                : d
                            )
                          );
                        }
                      }}
                    />
                    <Typography.Text>~</Typography.Text>
                    <Input
                      type="datetime-local"
                      value={
                        discounts
                          .find((d) => d.discountCategory === "RESERVATION")
                          ?.endDate?.slice(0, 16) || ""
                      }
                      onChange={(e) => {
                        const reserveDiscount = discounts.find(
                          (d) => d.discountCategory === "RESERVATION"
                        );
                        if (reserveDiscount) {
                          setDiscounts(
                            discounts.map((d) =>
                              d.discountCategory === "RESERVATION"
                                ? { ...d, endDate: e.target.value }
                                : d
                            )
                          );
                        }
                      }}
                    />
                  </div>
                </div>

                <div>
                  <Typography.Text type="secondary">예약할인가</Typography.Text>
                  <Typography.Text strong style={{ fontSize: 16, marginLeft: 8 }}>
                    {(() => {
                      const reserveDiscount = discounts.find(
                        (d) => d.discountCategory === "RESERVATION"
                      );
                      if (!reserveDiscount || !reserveDiscount.discountValue)
                        return "0원 (0원 할인)";
                      const salePrice = form.getFieldValue("baseSalePrice") || 0;
                      const discountedPrice =
                        reserveDiscount.discountType === "FIXED"
                          ? salePrice - reserveDiscount.discountValue
                          : salePrice * (1 - reserveDiscount.discountValue / 100);
                      return `${Math.max(0, discountedPrice).toLocaleString()}원 (${reserveDiscount.discountValue.toLocaleString()}${reserveDiscount.discountType === "FIXED" ? "원" : "%"} 할인)`;
                    })()}
                  </Typography.Text>
                </div>
              </div>
            )}

            <div style={{ marginTop: 32 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <Typography.Text strong>수량별 할인</Typography.Text>
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const lastMinQuantity =
                      volumePrices.length > 0
                        ? volumePrices[volumePrices.length - 1].minQuantity
                        : 0;
                    setVolumePrices([
                      ...volumePrices,
                      {
                        minQuantity: lastMinQuantity + 1,
                        discountType: "FIXED",
                        discountValue: 0,
                        isActive: true,
                      },
                    ]);
                  }}
                >
                  수량 할인 추가
                </Button>
              </div>

              {volumePrices.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                    특정 수량 이상 구매 시 판매가에서 할인이 적용됩니다. 최소 수량은 이전 값보다
                    커야 합니다.
                  </Typography.Text>

                  <Table
                    dataSource={volumePrices}
                    rowKey={(_, index) => `volume-${index}`}
                    pagination={false}
                    size="small"
                    scroll={{ x: 1000 }}
                    columns={[
                      {
                        title: "최소 수량",
                        dataIndex: "minQuantity",
                        width: 150,
                        render: (value, _record, index) => (
                          <AppInputNumber
                            value={value}
                            onChange={(val) => {
                              const newVolumePrices = [...volumePrices];
                              const prevMinQuantity =
                                index > 0 ? newVolumePrices[index - 1].minQuantity : 0;
                              const nextMinQuantity =
                                index < newVolumePrices.length - 1
                                  ? newVolumePrices[index + 1].minQuantity
                                  : Infinity;

                              const newValue = Math.max(prevMinQuantity + 1, val || 1);
                              if (newValue >= nextMinQuantity) {
                                toast("다음 항목의 최소 수량보다 작아야 합니다", "warning");
                                return;
                              }

                              newVolumePrices[index].minQuantity = newValue;
                              setVolumePrices(newVolumePrices);
                            }}
                            min={1}
                            style={{ width: "100%" }}
                          />
                        ),
                      },
                      {
                        title: "할인 금액/비율",
                        dataIndex: "discountValue",
                        render: (value, record, index) => (
                          <div style={{ display: "flex", gap: 8 }}>
                            <AppInputNumber
                              value={value}
                              onChange={(val) => {
                                const newVolumePrices = [...volumePrices];
                                newVolumePrices[index].discountValue = val || 0;
                                setVolumePrices(newVolumePrices);
                              }}
                              min={0}
                              style={{ flex: 1 }}
                            />
                            <Select
                              value={record.discountType}
                              onChange={(val) => {
                                const newVolumePrices = [...volumePrices];
                                newVolumePrices[index].discountType = val;
                                setVolumePrices(newVolumePrices);
                              }}
                              style={{ width: 80 }}
                            >
                              <Select.Option value="FIXED">원</Select.Option>
                              <Select.Option value="PERCENTAGE">%</Select.Option>
                            </Select>
                          </div>
                        ),
                      },
                      {
                        title: "활성",
                        dataIndex: "isActive",
                        width: 80,
                        render: (value, _record, index) => (
                          <Switch
                            checked={value}
                            onChange={(checked) => {
                              const newVolumePrices = [...volumePrices];
                              newVolumePrices[index].isActive = checked;
                              setVolumePrices(newVolumePrices);
                            }}
                            size="small"
                          />
                        ),
                      },
                      {
                        title: "삭제",
                        width: 80,
                        align: "center",
                        fixed: "right",
                        render: (_, record, index) => (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              setVolumePrices(volumePrices.filter((_, i) => i !== index));
                            }}
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      ),
    },
    {
      key: "detail",
      label: "상세 정보",
      children: (
        <>
          <Form.Item label="성분" name="ingredients">
            <Input.TextArea rows={3} placeholder="성분 입력" />
          </Form.Item>
          <Form.Item label="주의사항" name="precautions">
            <Input.TextArea rows={3} placeholder="주의사항 입력" />
          </Form.Item>
          <Form.Item label="관리가이드" name="careGuide">
            <Input.TextArea rows={3} placeholder="관리가이드 입력" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="상세 타입" name="detailType">
                <Select>
                  <Select.Option value="HTML">HTML</Select.Option>
                  <Select.Option value="IMAGE">이미지</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="PC 상세 내용" name="detailContent">
            <HtmlEditor placeholder="PC 상세 내용" />
          </Form.Item>
        </>
      ),
    },
    {
      key: "cert",
      label: "인증 및 출시 정보",
      children: (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="KC 인증" name="kcCertified" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="KC 인증번호" name="kcCertificationNumber">
                <AppInput placeholder="KC 인증번호" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="원산지" name="originCountry">
                <AppInput placeholder="예: 대한민국" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="제품 상태" name="productStatus">
                <Select>
                  <Select.Option value="NORMAL">정상</Select.Option>
                  <Select.Option value="DISCONTINUED">단종</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="제조일자" name="manufactureDate">
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="출시일자" name="releaseDate">
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: "addon",
      label: "추가상품 정보",
      children: <ProductAddonSection value={addonMappings} onChange={setAddonMappings} />,
    },
    optionPanel,
    {
      key: "display",
      label: "진열 정보",
      children: (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="진열 여부" name="isDisplay" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="진열 순서" name="displayOrder">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="진열 시작일" name="displayStartDate">
                <Input type="datetime-local" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="진열 종료일" name="displayEndDate">
                <Input type="datetime-local" />
              </Form.Item>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: "additional",
      label: "추가 정보",
      children: (
        <>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            리뷰 합침 상품
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            이 상품의 리뷰에 함께 표시할 다른 상품을 선택하세요.
          </Typography.Text>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 24 }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setReviewProductModalOpen(true)}
            >
              상품 선택
            </Button>
            {selectedReviewProducts.map((product) => (
              <Tag
                key={product.productId}
                closable
                onClose={() =>
                  setSelectedReviewProducts((prev) =>
                    prev.filter((p) => p.productId !== product.productId)
                  )
                }
              >
                {product.productName}
              </Tag>
            ))}
          </div>

          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            관련상품
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            이 상품과 관련된 상품을 선택하세요.
          </Typography.Text>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 24 }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setRelatedProductModalOpen(true)}
            >
              상품 선택
            </Button>
            {selectedRelatedProducts.map((product) => (
              <Tag
                key={product.productId}
                closable
                onClose={() =>
                  setSelectedRelatedProducts((prev) =>
                    prev.filter((p) => p.productId !== product.productId)
                  )
                }
              >
                {product.productName}
              </Tag>
            ))}
          </div>

          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            추천상품
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            이 상품과 함께 추천할 상품을 선택하세요.
          </Typography.Text>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setRecommendedProductModalOpen(true)}
            >
              상품 선택
            </Button>
            {selectedRecommendedProducts.map((product) => (
              <Tag
                key={product.productId}
                closable
                onClose={() =>
                  setSelectedRecommendedProducts((prev) =>
                    prev.filter((p) => p.productId !== product.productId)
                  )
                }
              >
                {product.productName}
              </Tag>
            ))}
          </div>
        </>
      ),
    },
  ]
    .filter(Boolean)
    .map((item) => ({ ...item, forceRender: true }));

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>상품 정보를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, width: "100%" }}>
      <Card>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          상품 수정
        </Typography.Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormValuesChange}
          onKeyDown={(e) => {
            // Enter 키로 form submit 방지 (명시적인 버튼 클릭만 허용)
            if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
              e.preventDefault();
            }
          }}
        >
          <Collapse defaultActiveKey={["basic"]} items={collapseItems as any} />

          {/* 제출 버튼 */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <CustomButton
                variant="outlined"
                onClick={() => navigate("/product")}
                style={{ width: "100%", height: "50px" }}
              >
                취소
              </CustomButton>
            </Col>
            <Col span={12}>
              <CustomButton
                variant="contained"
                disabled={submitting}
                style={{ width: "100%", height: "50px" }}
                onClick={async () => {
                  const result = await Swal.fire({
                    title: "상품을 수정하시겠습니까?",
                    text: "입력한 내용으로 상품 정보가 수정됩니다.",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonColor: "#1890ff",
                    cancelButtonColor: "#d9d9d9",
                    confirmButtonText: "수정",
                    cancelButtonText: "취소",
                  });
                  if (result.isConfirmed) {
                    form.submit();
                  }
                }}
              >
                {submitting ? "수정 중..." : "수정"}
              </CustomButton>
            </Col>
          </Row>
        </Form>
      </Card>

      <BrandSelectModal
        open={brandModalOpen}
        selectedId={selectedBrandId}
        onClose={() => setBrandModalOpen(false)}
        onConfirm={(brandId, brandName) => {
          setSelectedBrandId(brandId);
          setSelectedBrandName(brandName);
        }}
      />

      <CollectionSelectModal
        open={collectionModalOpen}
        selectedIds={selectedCollections.map((c) => c.collectionId)}
        onClose={() => setCollectionModalOpen(false)}
        onConfirm={(collections) => {
          setSelectedCollections(collections);
        }}
      />

      <ReviewIntegrationProductSelectModal
        open={reviewProductModalOpen}
        selectedIds={selectedReviewProducts.map((p) => p.productId)}
        currentProductId={id ? Number(id) : undefined}
        onClose={() => setReviewProductModalOpen(false)}
        onConfirm={(products) => {
          setSelectedReviewProducts(products);
        }}
      />

      <ReviewIntegrationProductSelectModal
        open={relatedProductModalOpen}
        selectedIds={selectedRelatedProducts.map((p) => p.productId)}
        currentProductId={id ? Number(id) : undefined}
        onClose={() => setRelatedProductModalOpen(false)}
        onConfirm={(products) => {
          setSelectedRelatedProducts(products);
        }}
      />

      <ReviewIntegrationProductSelectModal
        open={recommendedProductModalOpen}
        selectedIds={selectedRecommendedProducts.map((p) => p.productId)}
        currentProductId={id ? Number(id) : undefined}
        onClose={() => setRecommendedProductModalOpen(false)}
        onConfirm={(products) => {
          setSelectedRecommendedProducts(products);
        }}
      />
    </div>
  );
}
